'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Markdown from '@repo/ui/src/components/Markdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image/New';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { ProductCardProps } from './_ProductCard';
import React from 'react';
import { appConfig } from '../../utils/appConfig';

const useBadgeText = (badge?: string) => {
  const { t } = useTranslation();
  if (badge === 'new') {
    return t('productDetailPageContent.newText', undefined, { default: 'New' });
  }
  if (badge === 'sale') {
    return t('productDetailPageContent.saleText', undefined, { default: 'Sale' });
  }
};

const ProductCardMeater = ({
  title,
  price,
  mainLink,
  links,
  badge,
  image,
  primaryHeader,
  secondaryHeader,
}: Omit<ProductCardProps, 'hoverImage'>) => {
  const badgeText = useBadgeText(badge);
  if (!mainLink && !title) return null;
  const hasImage = image?.image?.url || image?.image?.id;
  const mobileImage = image;

  return (
    <Anchor
      {...mainLink}
      className={classNames(
          "tw-group tw-inline-block tw-w-full tw-h-full",
          appConfig.isTraeger ? 'tw-bg-neutral-100' : 'tw-bg-white'
      )}
      onClick={() =>
        navigationClicked({
          nav_type: 'Top Navigation',
          clicked_text: title ?? '',
          primary_header: primaryHeader ?? '',
          secondary_header: secondaryHeader ?? '',
          tertiary_header: title ?? '',
        })
      }
    >
      <div
        className={classNames(
          'tw-relative tw-text-neutral-700 tw-grid tw-gap-x-4 sm:tw-block',
          'tw-outline tw-outline-1 tw-outline-neutral-300 tw-p-2 sm:tw-p-4 tw-h-full',
          hasImage && 'sm:tw-grid-cols-[minmax(0,4fr)_minmax(0,5fr)] tw-grid-cols-[minmax(0,2fr)_minmax(0,7fr)] tw-items-center'
        )}
        role="group"
        aria-label="product"
      >
        {hasImage && (
          <div className="tw-overflow-visible tw-justify-self-start sm:tw-hidden tw-w-fit tw-h-20">
            <Image
              className="tw-w-20 tw-h-20 tw-object-cover"
              src={buildImageSrc(mobileImage, 96)}
              width={96}
              height={96}
              alt={mobileImage?.altTag ?? ''}
              data-testid="mobile-image"
            />
          </div>
        )}
        <div>
          <div className={classNames(
            'tw-flex tw-flex-row tw-justify-end',
            'sm:tw-static tw-absolute tw-right-4 tw-top-4',
            !badgeText && 'tw-invisible'
          )}>
            <div className="tw-px-2 tw-h-fit tw-bg-brand-300 tw-min-h-5 tw-py-[3px]">
              <p className="!tw-text-white tra-text-web-utl-label">{badgeText}</p>
            </div>
          </div>
          {hasImage && (
            <div className="tw-overflow-visible tw-hidden sm:tw-block">
              <div className="tw-relative tw-h-[160px] tw-w-full">
                <Image
                  className={classNames(
                    "tw-h-[155px] tw-w-full tw-object-contain",
                    "tw-transform tw-transition tw-duration-300",
                    "tw-ease-[cubic-bezier(0.33,1,0.68,1)] group-hover:tw-scale-110"
                  )}
                  src={buildImageSrc(image, 300)}
                  width={300}
                  height={300}
                  alt={image?.altTag ?? ''}
                  data-testid="desktop-image"
                />
              </div>
            </div>
          )}
          <h6
            className={classNames(
              "tw-mt-2 tw-font-bold",
              appConfig.isTraeger ? 'tra-text-web-sub-5' : 'tra-text-web-heading-4',
            )}
          >
            {title}
          </h6>
          {price && <Markdown className="!tw-mb-0 tw-py-2 tw-font-bold">{price}</Markdown>}
          <div className="tw-flex tw-flex-row tw-w-full tw-justify-start tw-gap-x-6">
            {links?.map((link, i) => (
              <Anchor
                {...link}
                key={i}
                className="tra-text-web-body-m-link"
                onClick={(e) => {
                  e.stopPropagation();
                  navigationClicked({
                    nav_type: 'Top Navigation',
                    clicked_text: link.displayName ?? '',
                    primary_header: primaryHeader ?? '',
                    secondary_header: secondaryHeader ?? '',
                    tertiary_header: `${title ? title + ' ' : ''}${link.displayName}`,
                  });
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Anchor>
  );
};

export default ProductCardMeater;
